import React, { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const SR = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;

export const CaseComposer = ({ onSend, busy, placeholder }) => {
  const [text, setText] = useState("");
  const [listening, setListening] = useState(false);
  const [voiceErr, setVoiceErr] = useState(null);
  const recRef = useRef(null);
  const baseRef = useRef("");
  useEffect(() => () => { try { recRef.current?.abort(); } catch { /* ignore */ } }, []);

  const toggleMic = () => {
    if (!SR) { setVoiceErr("Voice typing isn't supported in this browser. You can type instead."); return; }
    if (listening) { try { recRef.current?.stop(); } catch { /* ignore */ } return; }
    setVoiceErr(null);
    const rec = new SR();
    rec.lang = "en-US"; rec.interimResults = true; rec.continuous = true;
    baseRef.current = text ? text.trim() + " " : "";
    rec.onresult = (e) => {
      let fin = "", interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) fin += t; else interim += t;
      }
      if (fin) baseRef.current = (baseRef.current + fin + " ").replace(/\s+/g, " ");
      setText((baseRef.current + interim).replace(/^\s/, ""));
    };
    rec.onerror = (e) => {
      setListening(false);
      if (e.error === "not-allowed" || e.error === "service-not-allowed") setVoiceErr("Microphone access was blocked. You can type instead.");
    };
    rec.onend = () => setListening(false);
    recRef.current = rec;
    rec.start(); setListening(true);
  };

  const send = () => {
    const t = text.trim();
    if (!t || busy) return;
    try { recRef.current?.stop(); } catch { /* ignore */ }
    onSend(t); setText("");
  };

  return (
    <div className="rounded-xl border border-[#D9D6CC] bg-white p-3" data-testid="case-composer">
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder={placeholder}
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
        className="w-full resize-none text-base text-[#0B192C] outline-none placeholder:text-[#7A8794]" data-testid="case-input" />
      <div className="mt-2 flex items-center justify-between gap-2">
        <button type="button" onClick={toggleMic} aria-label={listening ? "Stop voice typing" : "Start voice typing"}
          className={`inline-flex h-11 items-center gap-2 rounded-full border px-4 text-sm font-medium ${listening ? "border-[#1E3E62] bg-[#1E3E62] text-white" : "border-[#BCCCDC] text-[#1E3E62]"}`}
          data-testid="case-mic-button">
          {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}{listening ? "Listening… tap to stop" : "Speak"}
        </button>
        <Button onClick={send} disabled={busy || !text.trim()} className="h-11 rounded-full bg-[#D4AF37] px-5 text-[#0B192C] hover:bg-[#c9a42f]" data-testid="case-send-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}<span className="ml-2">Send</span>
        </Button>
      </div>
      {voiceErr && <p className="mt-2 text-sm text-[#8A4B08]" data-testid="case-voice-error">{voiceErr}</p>}
    </div>
  );
};
